/* eslint-disable max-len */
import * as React from 'react';
import Timeline from '@mui/lab/Timeline';
import TimelineItem, { timelineItemClasses } from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import StepConnector, { stepConnectorClasses } from '@mui/material/StepConnector';
import PropTypes from 'prop-types';
import Typography from '@mui/material/Typography';
import { PiNotePencil } from 'react-icons/pi';
import styled from '@emotion/styled';
import { useMediaQuery, Stepper, Step, StepLabel } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { LanguageContext } from '../../../../../libs/Context';

// Styled components
const ColorTimeLine = styled('div')(() => ({
	backgroundColor : '#EFEFEF',
	zIndex          : 1,
	color           : 'var(--black)',
	width           : 40,
	height          : 40,
	display         : 'flex',
	borderRadius    : 'var(--pixel8)',
	justifyContent  : 'center',
	alignItems      : 'center',
	'& img'         : {
		width  : 'var(--pixel20)',
		height : 'var(--pixel20)'
	},
	'&.activeicon' : {
		backgroundColor : 'var(--primaryColor)',
		color           : 'var(--whiteColor)'
	}
}));

const ColorlibConnector = styled(StepConnector)(({ theme }) => ({
	[`&.${stepConnectorClasses.alternativeLabel}`] : {
		top : 22
	},
	[`&.${stepConnectorClasses.active}`] : {
		[`& .${stepConnectorClasses.line}`] : {
			backgroundColor : 'var(--primaryColor)'
		}
	},
	[`&.${stepConnectorClasses.completed}`] : {
		[`& .${stepConnectorClasses.line}`] : {
			backgroundColor : 'var(--primaryColor)'
		}
	},
	[`& .${stepConnectorClasses.line}`] : {
		height          : 2,
		border          : 0,
		backgroundColor : '#eaeaf0',
		borderRadius    : 1
	}
}));

const ColorlibStepIconRoot = styled('div')(() => ({
	backgroundColor : '#EFEFEF',
	zIndex          : 1,
	color           : 'var(--black)',
	width           : 50,
	height          : 50,
	display         : 'flex',
	borderRadius    : 'var(--pixel8)',
	justifyContent  : 'center',
	alignItems      : 'center'
}));

const ColorlibStepIconGrey = styled('div')(() => ({
	backgroundColor : 'var(--primaryColor)',
	zIndex          : 1,
	color           : 'var(--whiteColor)',
	width           : 50,
	height          : 50,
	display         : 'flex',
	borderRadius    : 'var(--pixel8)',
	justifyContent  : 'center',
	alignItems      : 'center'
}));

const ColorlibStepIcon = ({ className, icon, activeStep }) =>
{
	const icons = {
		1 : <img src='/images/info.svg' alt='img' className={`imgAvatarXS ${activeStep >= 1 ? 'invertWhite' : 'invertBlack'}`} />,
		2 : <img src='/images/icons/register.svg' alt='img' className={`imgAvatarXS ${activeStep >= 2 ? 'invertWhite' : 'invertBlack'}`} />,
		3 : <img src='/images/icons/branding.svg' alt='img' className={`imgAvatarXS ${activeStep >= 3 ? 'invertWhite' : 'invertBlack'}`} />,
		4 : <img src='/images/icons/speakers.svg' alt='img' className={`imgAvatarXS ${activeStep >= 4 ? 'invertWhite' : 'invertBlack'}`} />,
		5 : <img src='/images/icons/polling.svg' alt='img' className={`imgAvatarXS ${activeStep >= 5 ? 'invertWhite' : 'invertBlack'}`} />,
		6 : <img src='/images/icons/survey.svg' alt='img' className={`imgAvatarXS ${activeStep >= 6 ? 'invertWhite' : 'invertBlack'}`} />,
		7 : <img src='/images/icons/engagement.svg' alt='img' className={`imgAvatarXS ${activeStep >= 7 ? 'invertWhite' : 'invertBlack'}`} />
	};

	return activeStep >= Number(icon) - 1
		? (
			<ColorlibStepIconGrey className={`${className} mainHdngTxt`}>
				{icons[Number(icon)]}
			</ColorlibStepIconGrey>
		)
		: (
			<ColorlibStepIconRoot className={`${className} mainHdngTxt`}>
				{icons[Number(icon)]}
			</ColorlibStepIconRoot>
		);
};

ColorlibStepIcon.propTypes = {
	className  : PropTypes.string,
	icon       : PropTypes.node,
	activeStep : PropTypes.number.isRequired
};

const ScheduleTimeline = ({ activeStep }) =>
{
	const { language } = React.useContext(LanguageContext);
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

	const steps = [
		language?.createWebinarTxt,
		language?.webinarRegistration,
		language?.branding,
		language?.speakers,
		language?.webinarPolling,
		language?.webinarSurvey,
		language?.webinarEngagemenet
	];

	const data = [
		{ id: 1, content: language?.createWebinarTxt, icon: <PiNotePencil fontSize='var(--pixel20)' className={`imgAvatarXS ${activeStep >= 1 ? 'invertWhite' : 'invertBlack'}`} /> },
		{ id: 2, content: language?.webinarRegistration, icon: <img src='/images/icons/register.svg' alt='reg' className={`imgAvatarXS ${activeStep >= 2 ? 'invertWhite' : 'invertBlack'}`} /> },
		{ id: 3, content: language?.branding, icon: <img src='/images/icons/branding.svg' alt='brand' className={`imgAvatarXS ${activeStep >= 3 ? 'invertWhite' : 'invertBlack'}`} /> },
		{ id: 4, content: language?.speakers, icon: <img src='/images/icons/speakers.svg' alt='speak' className={`imgAvatarXS ${activeStep >= 4 ? 'invertWhite' : 'invertBlack'}`} /> },
		{ id: 5, content: language?.webinarPolling, icon: <img src='/images/icons/polling.svg' alt='poll' className={`imgAvatarXS ${activeStep >= 5 ? 'invertWhite' : 'invertBlack'}`} /> },
		{ id: 6, content: language?.webinarSurvey, icon: <img src='/images/icons/survey.svg' alt='survey' className={`imgAvatarXS ${activeStep >= 6 ? 'invertWhite' : 'invertBlack'}`} /> },
		{ id: 7, content: language?.webinarEngagemenet, icon: <img src='/images/icons/engagement.svg' alt='engage' className={`imgAvatarXS ${activeStep >= 7 ? 'invertWhite' : 'invertBlack'}`} /> }
	];

	const iconComp = (props) =>
	{
		return <ColorlibStepIcon {...props} activeStep={activeStep} />;
	};

	return (
		<>
			{!isMobile
				? <Timeline sx={{ [`& .${timelineItemClasses.root}:before`]: { flex: 0, padding: 0 } }}>

					{data.map((item, index) => (
						<TimelineItem
							key={(index + 1)}
							sx={{
								cursor : 'default',
								color  : (index + 1) === activeStep ? 'var(--primaryColor)' : 'inherit'
							}}
						>
							<TimelineSeparator>
								<ColorTimeLine className={activeStep >= (index + 1) ? 'activeicon' : ''}>{item.icon}</ColorTimeLine>
								{(index + 1) < data.length && <TimelineConnector sx={{ borderRight: activeStep - 1 >= ((index + 1)) ? '' : '2px dashed #D8DCE5', bgcolor: activeStep - 1 >= ((index + 1)) ? 'var(--primaryColor)' : 'transparent' }} />}
							</TimelineSeparator>
							<TimelineContent sx={{ py: 'var(--pixel12)', px: 2 }}>
								<Typography variant='inherit' component='span'>
									{item.content}
								</Typography>
							</TimelineContent>
						</TimelineItem>
					))}
				</Timeline>
				:			<Stepper alternativeLabel activeStep={activeStep} connector={<ColorlibConnector />} sx={{ width: 'var(--percent100)' }} className='hideScrollX'>
					{steps.map((label:any) => (
						<Step key={label}>
							<StepLabel StepIconComponent={iconComp}>
								{label}
							</StepLabel>
						</Step>
					))}
				</Stepper>}
		</>
	);
};

export default ScheduleTimeline;
